/**
 * The offers a cashier may apply.
 *
 * WHY THIS SCREEN IS PART OF THE CONTROL
 * --------------------------------------
 * A discount used to be a number a cashier typed, with nothing checking it.
 * An offer replaces that with a list somebody else decided on — so this is
 * where the deciding happens, and every limit set here is what holds a
 * discount in check once it reaches the till.
 *
 * WHICH IS WHY AN UNCAPPED PERCENTAGE IS QUESTIONED
 * -------------------------------------------------
 * "20% off" reads as a small thing and is a small thing on a Rs. 800 bill.
 * On a party booking of Rs. 200,000 it is Rs. 40,000. The server refuses to
 * create one without either a cap or somebody saying they meant it, and this
 * screen shows the figure rather than only the rule.
 *
 * WHAT THE COUNTS MEAN
 * --------------------
 * Times used comes from the orders themselves, not from the counter on the
 * offer. That counter is overwritten whenever a branch pulls; the discounts
 * recorded on real orders are what actually happened.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR, today } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { statusOf } from './offerStatus.js';

const KINDS = {
  percentage: 'Percent off',
  fixed: 'Rupees off',
  free_delivery: 'Free delivery',
};

function describe(offer) {
  if (offer.type === 'free_delivery') return 'Delivery free';
  const amount = offer.type === 'percentage' ? `${offer.value}%` : formatPKR(offer.value);
  const cap = offer.max_discount_amount ? ` (up to ${formatPKR(offer.max_discount_amount)})` : '';
  return `${amount} off${cap}`;
}

export function OffersTab({ canEdit }) {
  const [editing, setEditing] = useState(null);
  const queryClient = useQueryClient();

  const offers = useQuery({
    queryKey: ['menu', 'promotions'],
    queryFn: () => api.get('/menu/promotions'),
  });

  const stop = useMutation({
    mutationFn: (id) => api.delete(`/menu/promotions/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['menu', 'promotions'] }),
  });

  const rows = offers.data?.promotions ?? [];

  return (
    <div>
      {canEdit && (
        <div className="mb-3 flex justify-end">
          <Button onClick={() => setEditing({})}>Add an offer</Button>
        </div>
      )}

      {editing && (
        <OfferForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['menu', 'promotions'] });
            setEditing(null);
          }}
        />
      )}

      <DataTable
        rows={rows}
        loading={offers.isLoading}
        error={offers.isError}
        searchable
        searchKeys={['name', 'code']}
        columns={[
          { key: 'name', label: 'Offer', tone: () => 'text-ink' },
          {
            key: 'code',
            label: 'Code',
            render: (row) => row.code ?? '—',
            tone: () => 'font-mono text-ink-2',
          },
          { key: 'type', label: 'Takes off', render: describe },
          {
            key: 'status',
            label: 'State',
            render: (row) => statusOf(row).label,
            tone: (row) => statusOf(row).tone,
          },
          {
            key: 'timesUsed',
            label: 'Used',
            align: 'right',
            // Counted from real orders, not from the offer's own counter — a
            // pull overwrites that column.
            render: (row) =>
              row.max_uses ? `${row.timesUsed} of ${row.max_uses}` : String(row.timesUsed),
          },
          {
            key: 'givenAway',
            label: 'Given away',
            align: 'right',
            render: (row) => formatPKR(row.givenAway),
          },
          ...(canEdit
            ? [
                {
                  key: 'edit',
                  label: '',
                  align: 'right',
                  render: (row) => (
                    <span className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setEditing(row)}
                        className="text-sm text-ink-2 underline hover:text-ink"
                      >
                        Change
                      </button>
                      {row.is_active === 1 && (
                        <button
                          type="button"
                          onClick={() => stop.mutate(row.id)}
                          className="text-sm text-ink-3 underline hover:text-warn"
                        >
                          Stop
                        </button>
                      )}
                    </span>
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}

function OfferForm({ initial, onClose, onSaved }) {
  const isNew = !initial.id;

  const [name, setName] = useState(initial.name ?? '');
  const [code, setCode] = useState(initial.code ?? '');
  const [kind, setKind] = useState(initial.type ?? 'percentage');
  const [value, setValue] = useState(initial.value != null ? String(initial.value) : '');
  const [cap, setCap] = useState(
    initial.max_discount_amount != null ? String(initial.max_discount_amount) : ''
  );
  const [minimum, setMinimum] = useState(String(initial.min_order_amount ?? ''));
  const [startDate, setStartDate] = useState(initial.start_date ?? today());
  const [endDate, setEndDate] = useState(initial.end_date ?? '');
  const [maxUses, setMaxUses] = useState(initial.max_uses != null ? String(initial.max_uses) : '');
  const [acknowledgeNoCap, setAcknowledgeNoCap] = useState(false);

  const body = () => ({
    name: name.trim(),
    code: code.trim() || null,
    type: kind,
    value: kind === 'free_delivery' ? 0 : Number(value) || 0,
    maxDiscountAmount: cap === '' ? null : Number(cap),
    minOrderAmount: minimum === '' ? 0 : Number(minimum),
    startDate,
    endDate: endDate || null,
    maxUses: maxUses === '' ? null : Number(maxUses),
    acknowledgeNoCap,
  });

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? api.post('/menu/promotions', body())
        : api.put(`/menu/promotions/${initial.id}`, body()),
    onSuccess: onSaved,
  });

  const needsValue = kind !== 'free_delivery';
  const uncapped = kind === 'percentage' && cap === '';

  const ready =
    name.trim() &&
    startDate &&
    (!needsValue || Number(value) > 0) &&
    (!uncapped || acknowledgeNoCap) &&
    (!endDate || endDate >= startDate);

  const field =
    'h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

  return (
    <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
      <h3 className="mb-3 font-medium text-ink">{isNew ? 'New offer' : name || 'Change offer'}</h3>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="sm:col-span-2">
          <span className="mb-1.5 block text-xs text-ink-3">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Code a cashier types</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="TUESDAY20"
            className={`${field} font-mono uppercase`}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Kind</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            aria-label="Kind"
            className={field}
          >
            {Object.entries(KINDS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>

        {needsValue && (
          <label>
            <span className="mb-1.5 block text-xs text-ink-3">
              {kind === 'percentage' ? 'Percent off' : 'Rupees off'}
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label="Amount off"
              className={`${field} text-right tabular-nums`}
            />
          </label>
        )}

        {kind === 'percentage' && (
          <label>
            <span className="mb-1.5 block text-xs text-ink-3">Most it may take off</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              aria-label="Most it may take off"
              className={`${field} text-right tabular-nums`}
            />
          </label>
        )}

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Smallest bill it works on</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={minimum}
            onChange={(e) => setMinimum(e.target.value)}
            aria-label="Smallest bill"
            className={`${field} text-right tabular-nums`}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Starts</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label="Starts"
            className={field}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Ends — blank to run on</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label="Ends"
            className={field}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">
            How many times — blank for no limit
          </span>
          <input
            type="number"
            min="1"
            step="1"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            aria-label="How many times"
            className={`${field} text-right tabular-nums`}
          />
        </label>
      </div>

      {endDate && endDate < startDate && (
        <p className="mt-3 text-sm text-warn">This offer would end before it started.</p>
      )}

      {/*
        The figure, not only the rule. "20% off" reads as a small thing until
        somebody books a wedding.
      */}
      {uncapped && (
        <label className="mt-3 flex items-start gap-2 rounded-lg bg-warn-bg px-3 py-2.5">
          <input
            type="checkbox"
            checked={acknowledgeNoCap}
            onChange={(e) => setAcknowledgeNoCap(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-rule accent-brand-400"
          />
          <span className="text-sm text-warn">
            No maximum set. On a bill of {formatPKR(200000)} this would take off{' '}
            {formatPKR(((Number(value) || 0) * 200000) / 100)}. Tick to confirm you meant that.
          </span>
        </label>
      )}

      {save.isError && (
        <div className="mt-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">
          {save.error?.details?.length ? (
            <ul className="space-y-0.5">
              {save.error.details.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          ) : (
            (save.error?.message ?? 'That did not work.')
          )}
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>
          {save.isPending ? 'Saving' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
